<template>
  <div>
    <div class="hreg--card-info">
      <card-body class="row">
        <p>
          <i18n-t keypath="membersForm.intro" scope="global">
            <template v-slot:overviewLink>
              <a class="text-primary" target="_blank" rel="noreferrer noopener nofollow" :href="$t('membersForm.memPricingLink')">{{ $t("membersForm.memAssociation.overviewLink") }}</a>
            </template>
          </i18n-t>
        </p>
      </card-body>

      <card-footer title="" class="row">
        <Autocomplete
          name="associations"
          :label="$t('membersForm.memFooterTitle')"
          :error="contractDetailStore.errors.association"
          :suggestions="inputOptionStore.inputOptions.associations"
          :language="appStore.currentLanguage.toLowerCase()"
          v-model="membersStore.associationSelection"
          @validate="changeAssociation(membersStore.associationSelection)"
        />
        <div
          class="custom-checkbox"
          v-if="offerStore.showRetiredCheckbox"
        >
          <InputCheckbox
            v-model="offerStore.retiredCheckbox"
            name="cbOffer"
            :label="$t('offerForm.retired')"
            @validate="(event) => handleRetiredChange(event)"
          />
        </div>

        <small>
          <i18n-t keypath="membersForm.memAssociation.specialCondition" scope="global">
            <template v-slot:overviewLink>
              <a class="text-primary" target="_blank" rel="noreferrer noopener nofollow"
                :href="$t('identityForm.popoverAssocContractUrl')">{{ $t("membersForm.hereLinkText") }}</a>
            </template>
          </i18n-t>
        </small>

      </card-footer>
    </div>
    <button-box
      :disableBackBtn="true"
      :routeBack="routeHome"
      :routeForward="routeIdentity"
      :forwardBtnText="$t('form.button.forward')"
      class="col"
      :disableBtn="forwardDisabled"
    />
    <hr class="d--divider-h" />
    <div class="hreg--card-info" style="margin-top:80px">
      <card-body title="" class="row">
        <div class="h6"> {{ $t("membersForm.contractAdjustment") }} </div>
        <p>
          <i18n-t keypath="membersForm.contractAdjustmentText" scope="global">
            <template v-slot:mutationForm>
              <a tabindex="0" class="text-primary" target="_blank" rel="noreferrer noopener nofollow"
                :href="$t('membersForm.mutationFormUrl')"> {{ $t("membersForm.mutationForm") }}
              </a>
            </template>
          </i18n-t>
        </p>
      </card-body>
    </div>
    <Modal
      v-show="modalOpen"
      :header="$t('membersForm.modalMemberTitle', {association: associationName})"
      :bodyComponent="ModalBody"
      :bodyComponentProps="{association: associationName, url: associationUrl}"
      @close="modalOpen=false"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, watch }  from "vue";
import Autocomplete               from "@/components/form/Autocomplete.vue";
import CardBody                   from "@/components/CardBody.vue";
import CardFooter                 from "@/components/CardFooter.vue";
import CardHeader                 from "@/components/CardHeader.vue";
import ButtonBox                  from "@/components/ButtonBox.vue";
import Modal                      from "@/components/Modal.vue";
import ModalBody                  from "@/components/membership/ModalBody.vue";
import InputCheckbox              from "@/components/form/InputCheckbox.vue";

import { useIdentityStore }       from "@/store/IdentityStore";
import { useMembersStore }        from "@/store/MembersStore";
import { useAppStore }            from "@/store/AppStore";
import { useInputOptionStore }    from "@/store/InputOptionStore";
import { useContractDetailStore } from "@/store/ContractDetailStore";
import { useOfferStore }          from "@/store/OfferStore";
import { useVideoIdentStore }     from "@/store/VideoIdentStore";

const identityStore       = useIdentityStore();
const membersStore        = useMembersStore();
const appStore            = useAppStore();
const inputOptionStore    = useInputOptionStore();
const contractDetailStore = useContractDetailStore();
const offerStore          = useOfferStore();
const videoIdentStore     = useVideoIdentStore();

appStore.activeStep = appStore.screenNumbers.home;

// Variables + Values
const options = inputOptionStore.inputOptions;

// Text + String
const routeHome = appStore.routeHome;
const routeIdentity = appStore.routeIdentity;

const forwardDisabled = ref(false);
const modalOpen       = ref(false);
const associationName = ref("");
const associationUrl  = ref("");

onMounted(() => {
  // There was an issue that on page 6 "furtherInformation",
  // inputs from previous test in same browser tab were still present.
  // So, when user completes Product Finder, then in same tab
  // navigates to first page.
  // It looks as if values in "VideoIdentStore" survive one
  // "reload" despite the removal from sessionStorage.
  // As a work around, clear them, but keep some.
  const keepKeys = {
    profTitles: 1,
    professions: 1,
  };
  Object.keys(videoIdentStore?.inputs?.forPers).forEach(k => {
    if (!keepKeys.hasOwnProperty(k)) {
      delete videoIdentStore.inputs.forPers[k];
    }
  })
  videoIdentStore.inputs.forPers.professions = [""];
  videoIdentStore.inputs.forPers.profTitles = [];
  try {
    sessionStorage.removeItem("VideoIdentStore");
  } catch (error) {
    console.error(error);
  }
})

function changeAssociation(selected) {
  const lang = appStore.currentLanguage.toLowerCase();
  const assocFull = inputOptionStore.inputOptions.associations[lang].filter(
    item => item.key === selected)[0];
  identityStore.singleTeamForm = membersStore.hasUniqueDiscount;

  if (assocFull?.registrationLink?.de) {
    associationName.value = assocFull[lang];
    associationUrl.value  = assocFull.registrationLink[lang] || assocFull.registrationLink.de;
    forwardDisabled.value = true;
    modalOpen.value = true;
  }
  else {
    forwardDisabled.value = false;
  }
}

function handleRetiredChange(event) {
  offerStore.retiredCheckbox = event.target.value;
}


// Wait until `inputOptionStore` is ready and trigger
// conditional opening of modal. Just in case someone sets `CURACASA`
// for example and reloads the page "to get rid of the modal".
watch(() =>inputOptionStore.inputOptions.associations, (n, o) => {
  changeAssociation(membersStore.associationSelection);
});
</script>

<style scoped>
#membersDivider {
  height: auto;
  margin: 10px 1px 10px 1px;
}
#dropDownColor {
  font-family: "Lato", "Helvetica Neue", Helvetica, Arial, "Noto Sans",
    sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol",
    "Noto Color Emoji";
}

.padding-left {
  padding-left: 15px;
}

.pointer{
  cursor: pointer;
}
</style>
